/**
 * Where a request's email link lands — `/accept-invite?token=…` for a token
 * that is a company's request for more, not an invitation. (D2)
 *
 * Signed out, it names the company and what it asks, and asks the citizen to
 * sign in: the request is answered by the person it is about, on their own
 * account. Signed in, it finds the request in their own list and shows the
 * same review as Home — what they would and would not see, then Share / Not
 * now / Decline. A request no longer waiting for them (answered, withdrawn, or
 * another account's) says so.
 */
import { useTranslation } from '@tonnie/locale';
import { useMyInvitations, type InvitationPreview } from '@tonnie/sdk';
import { Button, Card, CardContent } from '@tonnie/ui';
import { useLocation } from 'wouter';

import { CompanyMark } from '@/features/platform/invitations/components/CompanyMark';

import { useShareRequestTitle } from '../hooks/use-share-request-title';
import { ShareRequestAnswerPanel } from './ShareRequestAnswerPanel';

export function ShareRequestLanding({
  token,
  preview,
  isSignedIn,
  onSignIn,
}: {
  token: string;
  preview: InvitationPreview & { request: NonNullable<InvitationPreview['request']> };
  isSignedIn: boolean;
  onSignIn: () => void;
}) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const title = useShareRequestTitle(preview.request, preview.companyName);
  // The citizen's own full list — a request put off with "Not now" is still theirs to answer.
  const mine = useMyInvitations(isSignedIn);
  const waiting = (mine.data ?? []).find((inv) => inv.token === token && inv.request !== null);

  return (
    <div className="mx-auto max-w-lg px-4 py-10">
      <Card data-testid="share-request-landing">
        <CardContent className="space-y-5 p-6">
          <div className="flex items-center gap-3">
            <CompanyMark name={preview.companyName} logo={preview.companyLogo} />
            <div className="min-w-0 space-y-1">
              <p className="text-2xs font-semibold uppercase tracking-wider text-primary-accessible">
                {t('invitations.request.eyebrow')}
              </p>
              <h1 className="text-lg font-semibold leading-snug text-foreground">{title}</h1>
            </div>
          </div>

          {!isSignedIn ? (
            <div className="space-y-3" data-testid="share-request-landing-signed-out">
              <p className="text-sm text-muted-foreground">
                {t('invitations.request.signInBody', { company: preview.companyName })}
              </p>
              <Button type="button" onClick={onSignIn} data-testid="share-request-sign-in">
                {t('invitations.request.signIn')}
              </Button>
            </div>
          ) : mine.isLoading ? null : waiting?.request ? (
            <ShareRequestAnswerPanel
              invitation={{ ...waiting, request: waiting.request }}
              onAnswered={() => setLocation('/dashboard')}
            />
          ) : (
            <div className="space-y-1" data-testid="share-request-landing-not-waiting">
              <p className="text-sm font-medium text-foreground">
                {t('invitations.request.notWaitingTitle')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('invitations.request.notWaitingBody')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
